import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessagesSquare } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { AdminNav } from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";

const fmtWhen = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) + " UTC" : "—");

const SUPPORT_LABELS = {
  explore: "Explore the transition",
  "create-self": "Create a ritual to hold herself",
  facilitate: "Irida facilitates the ritual",
  unsure: "Not yet sure",
};

const Detail = ({ label, value }) =>
  value ? (
    <p className="text-sm text-soft">
      <span className="text-xs tracking-[0.15em] uppercase text-clay">{label}: </span>
      {value}
    </p>
  ) : null;

export default function AdminEnquiries() {
  useSeo({ title: "Enquiries", noindex: true });
  const [token, setToken] = useState(localStorage.getItem("wd-admin") || "");
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState(null);
  const [handlingId, setHandlingId] = useState(null);

  const load = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { headers: { "X-Admin-Token": t } });
      setEnquiries(data.enquiries || []);
      setError(null);
    } catch {
      setEnquiries(null);
      setError("That key didn't work — check it and try again.");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, []);

  const saveToken = (t) => {
    setToken(t);
    localStorage.setItem("wd-admin", t);
    setError(null);
    if (t) load(t);
  };

  const markHandled = async (id) => {
    setHandlingId(id);
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status: "handled" }, { headers: { "X-Admin-Token": token } });
      setEnquiries((es) => es.map((e) => (e.id === id ? { ...e, status: "handled" } : e)));
    } catch {
      toast.error("Couldn't update the status — please try again.");
    } finally {
      setHandlingId(null);
    }
  };

  return (
    <main className="bg-cream min-h-screen" data-testid="admin-enquiries-page">
      <section className="mx-auto max-w-3xl px-6 pt-40 pb-24">
        <div className="flex items-center gap-3 text-clay">
          <MessagesSquare size={20} strokeWidth={1.25} />
          <p className="text-xs tracking-[0.35em] uppercase">Enquiries</p>
        </div>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Enquiries</h1>
        <p className="mt-4 text-base leading-relaxed text-soft">
          {enquiries === null
            ? "Enter your key to see contact-form and Ritual Consultation enquiries."
            : "Newest first. Ritual Consultation enquiries carry the ritual-consultation label."}
        </p>

        <div className="mt-8">
          <AdminNav current="/admin/enquiries" />
        </div>

        <div className="mt-10 max-w-xs">
          <label htmlFor="adm-token" className="text-xs tracking-[0.25em] uppercase text-soft">Your key</label>
          <input id="adm-token" type="password" value={token} onChange={(e) => saveToken(e.target.value)}
            placeholder="Admin key" className={inputCls} data-testid="enquiries-token-input" />
          {error && <p className="mt-2 text-sm text-rust" data-testid="enquiries-error">{error}</p>}
        </div>

        {enquiries !== null && enquiries.length === 0 && (
          <p className="mt-12 font-serif italic text-lg text-soft" data-testid="enquiries-empty">No enquiries yet.</p>
        )}

        <ul className="mt-12 space-y-0 divide-y divide-ink/8 border-t border-ink/15" data-testid="enquiries-list">
          {(enquiries || []).map((e, i) => (
            <motion.li key={e.id || i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="py-6" data-testid={`enquiry-row-${i}`}>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
                <p className="text-base text-ink">{e.name}</p>
                <p className="text-sm text-soft">{e.email}</p>
                <span
                  className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] tracking-[0.12em] uppercase ${
                    e.source === "ritual-consultation" ? "border-clay/40 bg-clay/10 text-clay" : "border-ink/15 text-soft/60"
                  }`}
                  data-testid={`enquiry-source-${i}`}
                >
                  {e.source === "ritual-consultation" ? "ritual-consultation" : "contact"}
                </span>
                <span
                  className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] tracking-[0.12em] uppercase ${
                    e.status === "handled" ? "border-sage/40 bg-sage/10 text-sage" : "border-clay/40 bg-clay/10 text-clay"
                  }`}
                  data-testid={`enquiry-status-${i}`}
                >
                  {e.status === "handled" ? "Handled" : "Open"}
                </span>
                {e.status !== "handled" && (
                  <button type="button" onClick={() => markHandled(e.id)} disabled={handlingId === e.id}
                    data-testid={`enquiry-handle-${i}`}
                    className="rounded-full border border-ink/20 px-4 py-1 text-xs tracking-[0.15em] uppercase text-soft transition-colors duration-300 hover:border-clay hover:text-ink disabled:opacity-50">
                    Mark handled
                  </button>
                )}
              </div>
              <p className="mt-1.5 text-xs tracking-[0.1em] uppercase text-soft/70">{fmtWhen(e.created_at)} · {e.lang || "en"}</p>
              <div className="mt-3 space-y-1">
                {e.source === "ritual-consultation" ? (
                  <>
                    <Detail label="To mark" value={e.transition} />
                    <Detail label="Ritual for" value={e.ritual_for} />
                    <Detail label="Date / period" value={e.date_period} />
                    <Detail label="Considering" value={SUPPORT_LABELS[e.support_option]} />
                    <Detail label="Phone" value={e.phone} />
                    <Detail label="Anything else" value={e.extra} />
                  </>
                ) : (
                  <>
                    <Detail label="Topic" value={e.topic} />
                    <Detail label="Phone" value={e.phone} />
                    <Detail label="Message" value={e.message} />
                  </>
                )}
              </div>
            </motion.li>
          ))}
        </ul>
      </section>
    </main>
  );
}
